export const FILTER_OPERATORS = {
  text: [
    { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true },
    { value: 'not_equals', label: 'is not equal to any of', requiresValue: true, multiValue: true },
    { value: 'contains', label: 'contains exactly', requiresValue: true },
    { value: 'not_contains', label: "doesn't contain", requiresValue: true },
    { value: 'starts_with', label: 'starts with', requiresValue: true },
    { value: 'ends_with', label: 'ends with', requiresValue: true },
    { value: 'is_known', label: 'is known', requiresValue: false },
    { value: 'is_unknown', label: 'is unknown', requiresValue: false }
  ],
  number: [
    { value: 'equals', label: 'is equal to', requiresValue: true },
    { value: 'not_equals', label: 'is not equal to', requiresValue: true },
    { value: 'greater_than', label: 'is greater than', requiresValue: true },
    { value: 'less_than', label: 'is less than', requiresValue: true },
    { value: 'greater_equal', label: 'is greater than or equal to', requiresValue: true },
    { value: 'less_equal', label: 'is less than or equal to', requiresValue: true },
    { value: 'between', label: 'is between', requiresValue: true, isBetween: true },
    { value: 'is_known', label: 'is known', requiresValue: false },
    { value: 'is_unknown', label: 'is unknown', requiresValue: false }
  ],
  date: [
    { value: 'equals', label: 'is equal to', requiresValue: true },
    { value: 'before', label: 'is before', requiresValue: true },
    { value: 'after', label: 'is after', requiresValue: true },
    { value: 'between', label: 'is between', requiresValue: true, isBetween: true },
    { value: 'in_last', label: 'in the last', requiresValue: true, isRelative: true },
    { value: 'in_next', label: 'in the next', requiresValue: true, isRelative: true },
    { value: 'is_today', label: 'is today', requiresValue: false },
    { value: 'is_yesterday', label: 'is yesterday', requiresValue: false },
    { value: 'is_tomorrow', label: 'is tomorrow', requiresValue: false },
    { value: 'is_known', label: 'is known', requiresValue: false },
    { value: 'is_unknown', label: 'is unknown', requiresValue: false }
  ],
  select: [
    { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true },
    { value: 'not_equals', label: 'is not equal to any of', requiresValue: true, multiValue: true },
    { value: 'is_known', label: 'is known', requiresValue: false },
    { value: 'is_unknown', label: 'is unknown', requiresValue: false }
  ],
  multiselect: [
    { value: 'contains_all', label: 'contains all of', requiresValue: true, multiValue: true },
    { value: 'contains_any', label: 'contains any of', requiresValue: true, multiValue: true },
    { value: 'not_contains', label: "doesn't contain", requiresValue: true, multiValue: true },
    { value: 'is_empty', label: 'is empty', requiresValue: false },
    { value: 'is_not_empty', label: 'is not empty', requiresValue: false }
  ],
  boolean: [
    { value: 'equals', label: 'is', requiresValue: true, booleanValue: true },
    { value: 'is_known', label: 'is known', requiresValue: false },
    { value: 'is_unknown', label: 'is unknown', requiresValue: false }
  ],
  reference: [
    { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true },
    { value: 'not_equals', label: 'is not equal to any of', requiresValue: true, multiValue: true },
    { value: 'is_known', label: 'is known', requiresValue: false },
    { value: 'is_unknown', label: 'is unknown', requiresValue: false }
  ]
};

// Map property types to operator groups
export const getOperatorsByType = (propertyType) => {
  const typeMapping = {
    'text': 'text',
    'textarea': 'text',
    'email': 'text',
    'phone': 'text',
    'url': 'text',
    'number': 'number',
    'currency': 'number',
    'percentage': 'number',
    'date': 'date',
    'datetime': 'date',
    'select': 'select',
    'multiselect': 'multiselect',
    'boolean': 'boolean',
    'user': 'reference',
    'contact': 'reference',
    'company': 'reference',
    'reference': 'reference',
    'file': 'reference'
  };

  const operatorGroup = typeMapping[propertyType] || 'text';
  return FILTER_OPERATORS[operatorGroup] || FILTER_OPERATORS['text'];
};

// Convert filter to MongoDB query
export const buildMongoQuery = (filterGroups, groupOperator = 'OR') => {
  if (!filterGroups || filterGroups.length === 0) return {};

  const groupQueries = filterGroups.map(group => {
    const filterQueries = group['filters'].map(filter => {
      const query = buildFilterQuery(filter);
      return query;
    }).filter(q => q !== null);

    if (filterQueries.length === 0) return null;
    if (filterQueries.length === 1) return filterQueries[0];
    
    return group['operator'] === 'AND' ? { $and: filterQueries } : { $or: filterQueries };
  }).filter(q => q !== null);

  if (groupQueries.length === 0) return {};
  if (groupQueries.length === 1) return groupQueries[0];
  
  return groupOperator === 'AND' ? { $and: groupQueries } : { $or: groupQueries };
};

// Helper: escape regex special characters
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Helper: check if property type needs case-insensitive regex matching
const isTextType = (type) => ['text', 'textarea', 'email', 'phone', 'url'].includes(type);

// Helper: check if property type is date/datetime
const isDateType = (type) => ['date', 'datetime'].includes(type);

// Helper: convert date string to day range (start and end of day)
const toDateRange = (value) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return { $gte: start, $lte: end };
};

// Build individual filter query
const buildFilterQuery = (filter) => {
  const { property, operator } = filter;
  // Boolean fields store their value in filter.boolean (not filter.value)
  const value = operator['booleanValue'] ? filter['boolean'] : filter['value'];

  // Handle custom fields vs system fields
  const fieldPath = filter['isCustomField'] ? `customFields.${property['name']}` : property['name'];
  const propType = property['type'] || 'text';

  switch (operator['value']) {
    case 'equals':
      if (typeof value === 'object') {
        let val = Object.keys(value).filter(key => value[key] && key);
        if (val.length === 0) return null;
        // For text types: use case-insensitive regex with partial match (contains)
        if (isTextType(propType)) {
          if (val.length === 1) return { [fieldPath]: { $regex: escapeRegex(val[0]), $options: 'i' } };
          // Multiple values: use $or with regex for each
          return { $or: val.map(v => ({ [fieldPath]: { $regex: escapeRegex(v), $options: 'i' } })) };
        }
        return { [fieldPath]: { $in: val } };
      }
      // Single value - handle by type
      if (isTextType(propType) && typeof value === 'string') return { [fieldPath]: { $regex: escapeRegex(value), $options: 'i' } };
      if (isDateType(propType) && value) {
        const range = toDateRange(value);
        return range ? { [fieldPath]: range } : null;
      }
      return { [fieldPath]: value };

    case 'not_equals':
      if (typeof value === 'object') {
        let val = Object.keys(value).filter(key => value[key] && key);
        if (val.length === 0) return null;
        // For text types: use case-insensitive regex negation
        if (isTextType(propType)) {
          if (val.length === 1) return { [fieldPath]: { $not: { $regex: escapeRegex(val[0]), $options: 'i' } } };
          // Multiple values: must not contain any of them
          return { $and: val.map(v => ({ [fieldPath]: { $not: { $regex: escapeRegex(v), $options: 'i' } } })) };
        }
        return { [fieldPath]: { $nin: val } };
      }
      if (isTextType(propType) && typeof value === 'string') return { [fieldPath]: { $not: { $regex: escapeRegex(value), $options: 'i' } } };
      if (isDateType(propType) && value) {
        const range = toDateRange(value);
        return range ? { $or: [{ [fieldPath]: { $lt: range.$gte } }, { [fieldPath]: { $gt: range.$lte } }] } : null;
      }
      return { [fieldPath]: { $ne: value } };

    case 'contains':
      return { [fieldPath]: { $regex: escapeRegex(value), $options: 'i' } };

    case 'not_contains':
      // Handle multiselect array values (same format as contains_any but negated)
      if (Array.isArray(value)) {
        if (value.length === 0) return null;
        return { [fieldPath]: { $nin: value } };
      }
      // Handle object format for multiselect fields (tags)
      if (typeof value === 'object') {
        const values = Object.keys(value).filter(key => value[key] && key);
        if (values.length === 0) return null;
        return { [fieldPath]: { $nin: values } };
      }
      // Handle string format for text fields - use regex negation
      return { [fieldPath]: { $not: { $regex: escapeRegex(value), $options: 'i' } } };

    case 'starts_with':
      return { [fieldPath]: { $regex: `^${escapeRegex(value)}`, $options: 'i' } };

    case 'ends_with':
      return { [fieldPath]: { $regex: `${escapeRegex(value)}$`, $options: 'i' } };

    case 'greater_than':
      return { [fieldPath]: { $gt: value } };

    case 'less_than':
      return { [fieldPath]: { $lt: value } };

    case 'greater_equal':
      return { [fieldPath]: { $gte: value } };

    case 'less_equal':
      return { [fieldPath]: { $lte: value } };

    case 'between':
      if (isDateType(propType)) {
        const minDate = new Date(value['min']);
        minDate.setHours(0, 0, 0, 0);
        const maxDate = new Date(value['max']);
        maxDate.setHours(23, 59, 59, 999);
        return { [fieldPath]: { $gte: minDate, $lte: maxDate } };
      }
      return { [fieldPath]: { $gte: value['min'], $lte: value['max'] } };

    case 'before':
      const beforeDate = new Date(value);
      beforeDate.setHours(0, 0, 0, 0);
      return { [fieldPath]: { $lt: beforeDate } };

    case 'after':
      const afterDate = new Date(value);
      afterDate.setHours(23, 59, 59, 999);
      return { [fieldPath]: { $gt: afterDate } };

    case 'in_last':
      const pastDate = getRelativeDate(-value['amount'], value['unit']);
      return { [fieldPath]: { $gte: pastDate, $lte: new Date() } };

    case 'in_next':
      const futureDate = getRelativeDate(value['amount'], value['unit']);
      return { [fieldPath]: { $gte: new Date(), $lte: futureDate } };

    case 'is_today':
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      return { [fieldPath]: { $gte: todayStart, $lte: todayEnd } };

    case 'is_yesterday':
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);
      return { [fieldPath]: { $gte: yesterday, $lte: yesterdayEnd } };

    case 'is_tomorrow':
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const tomorrowEnd = new Date(tomorrow);
      tomorrowEnd.setHours(23, 59, 59, 999);
      return { [fieldPath]: { $gte: tomorrow, $lte: tomorrowEnd } };

    case 'contains_all': {
      const allValues = Array.isArray(value) ? value : (typeof value === 'object' ? Object.keys(value).filter(k => value[k] && k) : [value]);
      if (allValues.length === 0) return null;
      return { [fieldPath]: { $all: allValues } };
    }

    case 'contains_any': {
      const anyValues = Array.isArray(value) ? value : (typeof value === 'object' ? Object.keys(value).filter(k => value[k] && k) : [value]);
      if (anyValues.length === 0) return null;
      return { [fieldPath]: { $in: anyValues } };
    }

    case 'is_empty':
      return { $or: [{ [fieldPath]: { $exists: false } }, { [fieldPath]: { $size: 0 } }] };

    case 'is_not_empty':
      return { [fieldPath]: { $exists: true, $ne: [], $not: { $size: 0 } } };

    case 'is_known':
      return { [fieldPath]: { $exists: true, $ne: null, $ne: '' } };

    case 'is_unknown':
      return { [fieldPath]: { $exists: false } };

    default:
      return null;
  }
};

// Helper function to calculate relative dates
const getRelativeDate = (amount, unit) => {
  const date = new Date();
  
  switch (unit) {
    case 'days':
      date.setDate(date.getDate() + amount);
      break;
    case 'weeks':
      date.setDate(date.getDate() + (amount * 7));
      break;
    case 'months':
      date.setMonth(date.getMonth() + amount);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() + amount);
      break;
  }
  
  return date;
};

// Get property icon based on type
export const getPropertyIcon = (propertyType) => {
  const iconMap = {
    'text': { icon: '', text: 'ABC' },
    'textarea': { icon: '', text: 'ABC' },
    'email': { icon: 'fa fa-envelope', text: '' },
    'phone': { icon: 'fa fa-phone', text: '' },
    'url': { icon: 'fa fa-link', text: '' },
    'number': { icon: '', text: '#' },
    'currency': { icon: '', text: '$' },
    'percentage': { icon: 'fa fa-percent', text: '' },
    'date': { icon: 'fa fa-calendar', text: '' },
    'datetime': { icon: 'fa fa-calendar', text: '' },
    'select': { icon: 'fa fa-list', text: '' },
    'multiselect': { icon: 'fa fa-list', text: '' },
    'boolean': { icon: 'fa fa-toggle-on', text: '' },
    'user': { icon: 'fa fa-user', text: '' },
    'contact': { icon: 'fa fa-address-book', text: '' },
    'company': { icon: 'fa fa-building', text: '' },
    'reference': { icon: 'fa fa-link', text: '' },
    'file': { icon: 'fa fa-file', text: '' }
  };
  
  return iconMap[propertyType] || { icon: 'fa fa-question', text: '' };
};